import { useState, useEffect } from 'react';

import { BackrsFormValues } from '@backrs_ui/common-ui';

import {
  useLazyFetchAutocompleteOptionsQuery,
  AutocompleteResult,
} from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';

import { Box, SxProps, Theme } from '@mui/material';
import { useForm } from 'react-hook-form';

import { AccountCreationLoading } from './AccountCreationLoading';
import { useOnboardingFormFields } from './hooks/useGetFormFields.hook';
import { OnboardingQuestion } from './onboarding.types';
import { OnboardingProgressBar, ProgressStep } from './OnboardingProgressBar';
import { OnboardingQuestionView } from './OnboardingQuestionView';
import { OnboardingSidebar, SidebarCard } from './OnboardingSidebar';
import { getStepKeyForField } from './questions/questions.config';
import { InviteFamilyStep } from './steps/InviteFamilyStep';
import { SubscriptionStep } from './SubscriptionStep';
import { VisibleStep } from './utils/getVisibleSteps';
import {
  triggerOnboardingValidation,
  restoreAutocompleteOptions,
  updateLocalAutocompleteOptions,
} from './utils/onboarding.utils';
import { StepKey, STEP_TO_SIDEBAR_INDEX } from './utils/stepNavigation';

type OnboardingPageProps = Readonly<{
  currentQuestion?: OnboardingQuestion;
  goToNextQuestion: (data: BackrsFormValues) => Promise<void>;
  currentStep: StepKey;
  setCurrentStep: (step: StepKey) => void;
  isShowingAccountCreationLoading: boolean;
  isShowingSubscription: boolean;
  isShowingInviteFamily: boolean;
  storedFormData?: BackrsFormValues | null;
  onSubscriptionSuccess: (sessionId?: string) => void;
  onBasicPlanContinue: () => void;
  onInviteFamilyComplete: () => void;
  isPaywallEnabled: boolean;
  isFamilyAccountsEnabled: boolean;
  visibleSteps: VisibleStep[];
  currentProgressStepIndex: number;
  onProgressStepClick: (stepIndex: number) => void;
  canNavigateBack: boolean;
  familyAccountSeats?: number;
}>;

export const OnboardingPage = ({
  currentQuestion,
  goToNextQuestion,
  currentStep,
  setCurrentStep,
  isShowingAccountCreationLoading,
  isShowingSubscription,
  isShowingInviteFamily,
  storedFormData,
  onSubscriptionSuccess,
  onBasicPlanContinue,
  onInviteFamilyComplete,
  isFamilyAccountsEnabled,
  visibleSteps,
  currentProgressStepIndex,
  onProgressStepClick,
  canNavigateBack,
  familyAccountSeats,
}: OnboardingPageProps) => {
  // Local state for autocomplete options; each option is keyed by the API endpoint
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<AutocompleteResult[]>([]);

  const [triggerFetchAutocomplete, { data: autocompleteData }] =
    useLazyFetchAutocompleteOptionsQuery();

  // Initialize a form instance with onSubmit validation mode
  const formMethods = useForm<BackrsFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: {
      ...(storedFormData || {}),
    },
  });

  const handleInputChange = (input: string, endpoint: string) => {
    triggerFetchAutocomplete({ endpoint, searchTerm: input });
  };

  // Update local autocomplete options when new API data arrives
  useEffect(() => {
    updateLocalAutocompleteOptions({
      autocompleteData,
      autoCompleteOptions,
      setAutoCompleteOptions,
    });
  }, [autoCompleteOptions, autocompleteData]);

  // Restore autocomplete options for fields that have values when navigating back
  useEffect(() => {
    restoreAutocompleteOptions({
      currentQuestion,
      formMethods,
      autoCompleteOptions,
      setAutoCompleteOptions,
    });
  }, [currentStep, currentQuestion, formMethods, autoCompleteOptions]);

  // Trigger validation for fields that have existing values when navigating back
  useEffect(() => {
    triggerOnboardingValidation({
      currentQuestion,
      formMethods,
      currentStep,
    });
  }, [currentStep, currentQuestion, formMethods]);

  // Use the custom hook to generate the form fields based on the current question
  const fields = useOnboardingFormFields({
    currentQuestion,
    currentStep,
    autoCompleteOptions,
    setAutoCompleteOptions,
    handleInputChange,
  });

  const STEP_TO_SIDEBAR_CARD: Partial<Record<StepKey, SidebarCard>> = {
    ROLE_SELECTION: 'nesha',
    COMPLETE_ACCOUNT: 'data-locked',
    ACTIVATION_CODE: 'matteo',
  };

  const determineSidebarCard = (): SidebarCard => {
    // View-based cards take priority over step-based cards
    if (isShowingInviteFamily) return 'sayvon';
    if (isShowingSubscription) return 'aurella';

    return STEP_TO_SIDEBAR_CARD[currentStep] || undefined;
  };

  const handleClearEmptyFields = (data: BackrsFormValues) => {
    return Object.fromEntries(
      Object.entries(data).filter(([, value]) => {
        if (Array.isArray(value)) return value.length > 0;

        return value !== '' && value !== null && value !== undefined;
      }),
    );
  };

  const showQuestion =
    currentQuestion &&
    !isShowingAccountCreationLoading &&
    !isShowingSubscription &&
    !isShowingInviteFamily;

  // Determine current consolidated step index (0-3 matching sidebar items)
  // Uses STEP_TO_SIDEBAR_INDEX with special handling for view states
  const getCurrentSidebarIndex = (): number => {
    if (isShowingAccountCreationLoading) {
      return 4; // Past all steps
    }

    if (isShowingInviteFamily) {
      return 3; // Invite family view
    }

    if (isShowingSubscription) {
      return 2; // Subscription view maps to "Select your plan"
    }

    return STEP_TO_SIDEBAR_INDEX[currentStep];
  };

  const currentConsolidatedStep = getCurrentSidebarIndex();

  // Create 4 fixed progress steps matching sidebar items
  // Filter out "Invite family" if family accounts are disabled
  const consolidatedStepKeys = isFamilyAccountsEnabled
    ? ['CREATE_ACCOUNT', 'YOUR_ROLE', 'SELECT_PLAN', 'INVITE_FAMILY']
    : ['CREATE_ACCOUNT', 'YOUR_ROLE', 'SELECT_PLAN'];

  const progressSteps: ProgressStep[] = consolidatedStepKeys.map((key, idx) => ({
    key,
    index: idx,
    isCompleted: idx < currentConsolidatedStep,
    isCurrent: idx === currentConsolidatedStep,
    // Step 0 (Create account) is never clickable - can't go back to signup
    // Other steps are clickable if completed and back navigation is allowed
    isClickable: canNavigateBack && idx > 0 && idx < currentConsolidatedStep,
  }));

  // Handle consolidated progress step click - map to the appropriate navigation
  const handleConsolidatedStepClick = (consolidatedIndex: number) => {
    // Map consolidated step index to visible step key, then find and click that step
    const stepKeyMap: Record<number, string> = {
      1: 'ROLE_SELECTION', // Your role → first step in that section
      2: 'ACTIVATION_CODE', // Select your plan → first step in that section
      3: 'INVITE_FAMILY', // Invite family
    };

    const targetStepKey = stepKeyMap[consolidatedIndex];

    if (!targetStepKey) return;

    // Find the index of this step in visibleSteps
    const visibleStepIndex = visibleSteps.findIndex((step) => step.key === targetStepKey);

    if (visibleStepIndex >= 0) {
      onProgressStepClick(visibleStepIndex);
    }
  };

  return (
    <Box sx={containerStyle}>
      <OnboardingSidebar
        currentStep={currentStep}
        isShowingAccountCreationLoading={isShowingAccountCreationLoading}
        isShowingInviteFamily={isShowingInviteFamily}
        sidebarCard={determineSidebarCard()}
        showInviteFamily={isFamilyAccountsEnabled}
        visibleSteps={visibleSteps}
        currentProgressStepIndex={currentProgressStepIndex}
        canNavigateBack={canNavigateBack}
        onProgressStepClick={onProgressStepClick}
      />

      <Box sx={contentContainerSx}>
        {isShowingAccountCreationLoading && <AccountCreationLoading />}

        {isShowingSubscription && (
          <SubscriptionStep
            onSubscriptionSuccess={onSubscriptionSuccess}
            onBasicPlanContinue={onBasicPlanContinue}
          />
        )}

        {isShowingInviteFamily && (
          <InviteFamilyStep onComplete={onInviteFamilyComplete} maxSeats={familyAccountSeats} />
        )}

        {/* WE CANNOT CONDITIONALLY RENDER THE FORM WHEN LOADING, OTHERWISE WE BREAK RHF ERROR STATE */}
        {showQuestion && (
          <OnboardingQuestionView
            currentQuestion={currentQuestion}
            fields={fields}
            formMethods={formMethods}
            goToNextQuestion={goToNextQuestion}
            handleClearEmptyFields={handleClearEmptyFields}
            setCurrentStep={setCurrentStep}
            getStepKeyForField={getStepKeyForField}
            currentStep={currentStep}
          />
        )}

        <OnboardingProgressBar steps={progressSteps} onStepClick={handleConsolidatedStepClick} />
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  margin: '0 auto',
  backgroundColor: backrs.colors['Colors/Base/White'],
  width: '100%',
  height: '100dvh',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
});

const contentContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
  width: '100%',
  height: '100dvh',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
};
